import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Calendar, Clock, ToggleLeft, ToggleRight, Repeat, ChevronDown, X } from 'lucide-react';
import './MeetingDays.scss';
import { localTimeFormatted } from '../../../utils/systemSettings';

const MeetingDays = ({
  title = "Meeting Schedule",
  startDate,
  endDate,
  allDay = false,
  recurringRule = '',
  onChange,
  className
}) => {
  const [internalStartDate, setInternalStartDate] = useState(null);
  const [internalEndDate, setInternalEndDate] = useState(null);
  const [isAllDay, setIsAllDay] = useState(allDay);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('Weekly');
  const [selectedDays, setSelectedDays] = useState([]);
  const [monthlyOption, setMonthlyOption] = useState('dayOfMonth'); // 'dayOfMonth' or 'dayOfWeek'
  const [yearlyOption, setYearlyOption] = useState('dayOfMonth'); // 'dayOfMonth' or 'dayOfWeek'
  const [untilDate, setUntilDate] = useState(null);

  const settings = JSON.parse(localStorage.getItem("settings"));
  let dateFormat = settings?.regionalSettings?.dateFormat;

  const timeFormat = "HH:mm"; //forcing format scince library has a bug on hh:mm a format
  const frequencyOptions = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
  const daysOfWeek = [
    { label: 'Sun', value: 0 },
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
  ];

  useEffect(() => {
    setInternalStartDate(startDate ? moment.utc(startDate).local().toDate(): null);
    setInternalEndDate(endDate ? moment.utc(endDate).local().toDate() : null);
    setIsAllDay(allDay);
    
    // Set default until date to 3 months from start date
    const defaultUntilDate = startDate ? moment(startDate).add(1, 'months').toDate() : null;
    setUntilDate(defaultUntilDate);
    
    if (recurringRule) {
      parseRecurringRule(recurringRule);
    }
  }, [startDate, endDate, allDay, recurringRule]);

  const parseRecurringRule = (rule) => {
    if (rule.includes('FREQ=')) {
      setIsRecurring(true);
      
      if (rule.includes('FREQ=DAILY')) setFrequency('Daily');
      else if (rule.includes('FREQ=WEEKLY')) setFrequency('Weekly');
      else if (rule.includes('FREQ=MONTHLY')) setFrequency('Monthly');
      else if (rule.includes('FREQ=YEARLY')) setFrequency('Yearly');
      
      // Parse BYDAY for weekly
      const bydayMatch = rule.match(/BYDAY=([^;]+)/);
      if (bydayMatch && rule.includes('FREQ=WEEKLY')) {
        const days = bydayMatch[1].split(',');
        const dayNumbers = days.map(day => {
          switch(day) {
            case 'SU': return 0;
            case 'MO': return 1;
            case 'TU': return 2;
            case 'WE': return 3;
            case 'TH': return 4;
            case 'FR': return 5;
            case 'SA': return 6;
            default: return -1;
          }
        }).filter(d => d !== -1);
        setSelectedDays(dayNumbers);
      }

      // Parse monthly options
      if (rule.includes('FREQ=MONTHLY')) {
        if (bydayMatch) {
          setMonthlyOption('dayOfWeek');
        } else {
          setMonthlyOption('dayOfMonth');
        }
      }

      // Parse yearly options
      if (rule.includes('FREQ=YEARLY')) {
        if (bydayMatch) {
          setYearlyOption('dayOfWeek');
        } else {
          setYearlyOption('dayOfMonth');
        }
      }
      
      const untilMatch = rule.match(/UNTIL=([^;]+)/);
      if (untilMatch) {
        const until = moment(untilMatch[1]).toDate();
        setUntilDate(until);
      }
    }
  };

  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const getWeekOfMonth = (date) => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstWeekday = firstDayOfMonth.getDay();
    const dayOfMonth = date.getDate();
    
    // Calculate which occurrence of the weekday this is
    const adjustedDate = dayOfMonth + firstWeekday - 1;
    const weekOfMonth = Math.floor(adjustedDate / 7) + 1;
    
    return Math.min(weekOfMonth, 4); // Cap at 4th occurrence to avoid issues
  };

  const getOrdinalWord = (num) => {
    switch(num) {
      case 1: return 'first';
      case 2: return 'second';
      case 3: return 'third';
      case 4: return 'fourth';
      case 5: return 'fifth';
      default: return `${num}th`;
    }
  };

  const getDayName = (dayNum) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNum];
  };

  const getMonthName = (monthNum) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthNum];
  };

  const getDayAbbreviation = (dayNum) => {
    const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    return days[dayNum];
  };

  const generateRecurringRule = (force = false, updatedData={}) => {
    if (!force && !isRecurring) return '';
    
    const data = {
      frequency: frequency,
      selectedDays: selectedDays,
      monthlyOption: monthlyOption,
      yearlyOption: yearlyOption,
      untilDate: untilDate,
      startDate: internalStartDate,
      ...updatedData
    };

    let rule = `FREQ=${data.frequency.toUpperCase()}`;
    
    // Weekly recurrence
    if (data.frequency === 'Weekly' && data.selectedDays.length > 0) {
      const dayStrings = data.selectedDays.map(day => getDayAbbreviation(day)).filter(d => d !== '');
      if (dayStrings.length > 0) {
        rule += `;BYDAY=${dayStrings.join(',')}`;
      }
    }

    // Monthly recurrence
    if (data.frequency === 'Monthly' && data.startDate) {
      const date = new Date(data.startDate);
      if (data.monthlyOption === 'dayOfMonth') {
        rule += `;BYMONTHDAY=${date.getDate()}`;
      } else if (data.monthlyOption === 'dayOfWeek') {
        const weekOfMonth = getWeekOfMonth(date);
        const dayOfWeek = getDayAbbreviation(date.getDay());
        rule += `;BYDAY=${weekOfMonth}${dayOfWeek}`;
      }
    }

    // Yearly recurrence
    if (data.frequency === 'Yearly' && data.startDate) {
      const date = new Date(data.startDate);
      rule += `;BYMONTH=${date.getMonth() + 1}`;
      
      if (data.yearlyOption === 'dayOfMonth') {
        rule += `;BYMONTHDAY=${date.getDate()}`;
      } else if (data.yearlyOption === 'dayOfWeek') {
        const weekOfMonth = getWeekOfMonth(date);
        const dayOfWeek = getDayAbbreviation(date.getDay());
        rule += `;BYDAY=${weekOfMonth}${dayOfWeek}`;
      }
    }
    
    if (data.untilDate) {
      rule += `;UNTIL=${moment(data.untilDate).utc().format('YYYYMMDD[T]HHmmss[Z]')}`;
    }
    
    return rule;
  };

  const updateMeetingData = (updates) => {
    const newData = {
      startDate: internalStartDate ?moment(internalStartDate).utc().format(): null,
      endDate: internalEndDate ? moment(internalEndDate).utc().format(): null,
      allDay: isAllDay,
      recurringRule: generateRecurringRule(),
      ...updates
    };
    onChange(newData);
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const newStartDate = new Date(date);
      setInternalStartDate(newStartDate);
      
      if (!internalEndDate || newStartDate > internalEndDate) {
        setInternalEndDate(newStartDate);
        updateMeetingData({
          startDate: moment(newStartDate).utc().format(),
          endDate: moment(newStartDate).utc().format(),
        });
      } else {
        updateMeetingData({
          startDate: moment(newStartDate).utc().format(),
        });
      }
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      const newEndDate = new Date(date);
      setInternalEndDate(newEndDate);
      updateMeetingData({
        endDate: moment(newEndDate).utc().format(),
      });
    }
  };

  const handleAllDayToggle = () => {
    const newAllDay = !isAllDay;
    setIsAllDay(newAllDay);
    
    if (newAllDay) {
      const startOfDay = internalStartDate ? new Date(internalStartDate) : new Date();
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = internalEndDate ? new Date(internalEndDate): new Date();
      endOfDay.setHours(23, 59, 59, 999);
      
      setInternalStartDate(startOfDay);
      setInternalEndDate(endOfDay);
      
      updateMeetingData({
        startDate: moment(startOfDay).utc().format(),
        endDate: moment(endOfDay).utc().format(),
        allDay: newAllDay,
      });
    } else {
      updateMeetingData({
        allDay: newAllDay,
      });
    }
  };

  const handleRecurringToggle = () => {
    const newRecurring = !isRecurring;
    setIsRecurring(newRecurring);
    if (!newRecurring) {
      setFrequency('Weekly');
      setSelectedDays([]);
      let startDate;
      if(internalStartDate)
        startDate = internalStartDate;
      else{
        startDate = new Date();
        handleStartDateChange(startDate);
      }
      setUntilDate(moment(startDate).add(3, 'months').toDate());
      updateMeetingData({ recurringRule: ""});
    }
    else{
      updateMeetingData({ recurringRule: generateRecurringRule(true)});
    }
  };

  const handleFrequencyChange = (newFrequency) => {
    setFrequency(newFrequency);
    let newSelectedDays = selectedDays;
    
    if (newFrequency !== 'Weekly') {
      newSelectedDays = [];
      setSelectedDays([]);
    }
    
    // Set default options for monthly and yearly
    if (newFrequency === 'Monthly') {
      setMonthlyOption('dayOfMonth');
    }
    if (newFrequency === 'Yearly') {
      setYearlyOption('dayOfMonth');
    }
    
    updateMeetingData({
      recurringRule: generateRecurringRule(false, { 
        frequency: newFrequency, 
        selectedDays: newSelectedDays,
        monthlyOption: newFrequency === 'Monthly' ? 'dayOfMonth' : monthlyOption,
        yearlyOption: newFrequency === 'Yearly' ? 'dayOfMonth' : yearlyOption
      })
    });
  };

  const handleDayToggle = (dayValue) => {
    const newSelectedDays = selectedDays.includes(dayValue)
      ? selectedDays.filter(d => d !== dayValue)
      : [...selectedDays, dayValue].sort();
    
    setSelectedDays(newSelectedDays);
    updateMeetingData({recurringRule : generateRecurringRule(false , { selectedDays: newSelectedDays})});
  };

  const handleUntilDateChange = (date) => {
    setUntilDate(date);
    updateMeetingData({recurringRule : generateRecurringRule(false , { untilDate: date})});

  };

  const handleMonthlyOptionChange = (option) => {
    setMonthlyOption(option);
    updateMeetingData({
      recurringRule: generateRecurringRule(false, { monthlyOption: option })
    });
  };

  const handleYearlyOptionChange = (option) => {
    setYearlyOption(option);
    updateMeetingData({
      recurringRule: generateRecurringRule(false, { yearlyOption: option })
    });
  };

  const isSameDay = internalStartDate && internalStartDate && moment(internalStartDate).isSame(moment(internalEndDate), 'day');

  const formatDate = (date) => date ? moment(date).format(dateFormat ? dateFormat.replace(/dd/g, 'DD').replace(/yy/g, 'YY') : dateFormat) : "";
  const formatDateTime = (date) => date ? moment(date).format(`${dateFormat ? dateFormat.replace(/dd/g, 'DD').replace(/yy/g, 'YY') : dateFormat} ${timeFormat}`) : ""; 

  const renderRecurrenceOptions = () => {
    if (!internalStartDate) return null;
    
    const date = new Date(internalStartDate);
    const dayOfMonth = date.getDate();
    const dayOfWeek = date.getDay();
    const weekOfMonth = getWeekOfMonth(date);
    const monthName = getMonthName(date.getMonth());
    const dayName = getDayName(dayOfWeek);
    const ordinalWord = getOrdinalWord(weekOfMonth);

    if (frequency === 'Weekly') {
      return (
        <div className="flex items-start gap-2">
          <label className="text-xs font-medium text-gray-700 whitespace-nowrap min-w-[60px] pt-2">
            Days
          </label>
          <div className="flex flex-wrap gap-1">
            {daysOfWeek.map(day => (
              <button
                key={day.value}
                onClick={() => handleDayToggle(day.value)}
                className={`px-2 py-1 text-xs rounded border transition-all duration-200 ${
                  selectedDays.includes(day.value)
                    ? 'border-transparent text-white'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
                style={{
                  backgroundColor: selectedDays.includes(day.value) ? '#342c82' : 'white'
                }}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (frequency === 'Monthly') {
      return (
        <div className="flex items-start gap-2">
          <label className="text-xs font-medium text-gray-700 whitespace-nowrap min-w-[60px] pt-2">
            Repeat
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="monthlyOption"
                value="dayOfMonth"
                checked={monthlyOption === 'dayOfMonth'}
                onChange={() => handleMonthlyOptionChange('dayOfMonth')}
                className="w-3 h-3"
                style={{
                  accentColor: '#342c82'
                }}
              />
              <span className="text-xs text-gray-700">
                On day {dayOfMonth}{getOrdinalSuffix(dayOfMonth)}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="monthlyOption"
                value="dayOfWeek"
                checked={monthlyOption === 'dayOfWeek'}
                onChange={() => handleMonthlyOptionChange('dayOfWeek')}
                className="w-3 h-3"
                style={{
                  accentColor: '#342c82'
                }}
              />
              <span className="text-xs text-gray-700">
                On the {ordinalWord} {dayName}
              </span>
            </label>
          </div>
        </div>
      );
    }

    if (frequency === 'Yearly') {
      return (
        <div className="flex items-start gap-2">
          <label className="text-xs font-medium text-gray-700 whitespace-nowrap min-w-[60px] pt-2">
            Repeat
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="yearlyOption"
                value="dayOfMonth"
                checked={yearlyOption === 'dayOfMonth'}
                onChange={() => handleYearlyOptionChange('dayOfMonth')}
                className="w-3 h-3"
                style={{
                  accentColor: '#342c82'
                }}
              />
              <span className="text-xs text-gray-700">
                On {monthName} {dayOfMonth}{getOrdinalSuffix(dayOfMonth)}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="yearlyOption"
                value="dayOfWeek"
                checked={yearlyOption === 'dayOfWeek'}
                onChange={() => handleYearlyOptionChange('dayOfWeek')}
                className="w-3 h-3"
                style={{
                  accentColor: '#342c82'
                }}
              />
              <span className="text-xs text-gray-700">
                On the {ordinalWord} {dayName} of {monthName}
              </span>
            </label>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`meeting-days-wrapper bg-white border border-gray-300 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" style={{ color: '#342c82' }} />
          <span className="text-sm font-semibold text-gray-800">{title}</span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 font-medium">Recurring</span>
            <button
              onClick={handleRecurringToggle}
              className="flex items-center transition-colors duration-200 hover:opacity-80"
            >
              {isRecurring ? (
                <ToggleRight className="w-6 h-6" style={{ color: '#342c82' }} />
              ) : (
                <ToggleLeft className="w-6 h-6 text-gray-400" />
              )}
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 font-medium">All day</span>
            <button
              onClick={handleAllDayToggle}
              className="flex items-center transition-colors duration-200 hover:opacity-80"
            >
              {isAllDay ? (
                <ToggleRight className="w-6 h-6" style={{ color: '#342c82' }} />
              ) : (
                <ToggleLeft className="w-6 h-6 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
            {isAllDay ? 'Start Date' : 'Start'}
          </label>
          <div className="relative">
            <DatePicker
              selected={internalStartDate}
              onChange={handleStartDateChange}
              showTimeSelect={!isAllDay}
                timeFormat={timeFormat}
                timeIntervals={15}
                dateFormat={!isAllDay ?`${dateFormat} ${timeFormat}` : dateFormat}
              className="dateTime-input"
              style={{ 
                backgroundColor: '#f5f5f5',
                minWidth: isAllDay ? '120px' : '200px'
              }}
              timeCaption="Time"
              popperClassName="react-datepicker-popper"
              popperPlacement="bottom-start"
              showTimeSelectOnly={false}
              timeInputLabel="Time:"
              withPortal={false}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              {isAllDay ? (
                <Calendar className="w-3 h-3 text-gray-400" />
              ) : (
                <Clock className="w-3 h-3 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {!(isAllDay && isSameDay) && (
          <>
            <span className="text-gray-400 text-sm">to</span>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
                {isAllDay ? 'End Date' : 'End'}
              </label>
              <div className="relative">
                <DatePicker
                  selected={internalEndDate}
                  onChange={handleEndDateChange}
                  showTimeSelect={!isAllDay}
                  timeFormat={timeFormat}
                timeIntervals={15}
                dateFormat={!isAllDay ?`${dateFormat} ${timeFormat}` : dateFormat}
                  minDate={internalStartDate}
                  className="dateTime-input"
                  style={{ 
                    backgroundColor: '#f5f5f5',
                    minWidth: isAllDay ? '120px' : '200px'
                  }}
                  timeCaption="Time"
                  popperClassName="react-datepicker-popper"
                  popperPlacement="bottom-start"
                  showTimeSelectOnly={false}
                  timeInputLabel="Time:"
                  withPortal={false}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  {isAllDay ? (
                    <Calendar className="w-3 h-3 text-gray-400" />
                  ) : (
                    <Clock className="w-3 h-3 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {isRecurring && (
        <div className="mt-4 p-3 rounded border" style={{ backgroundColor: '#f5f5f5', borderColor: '#e5e7eb' }}>
          <div className="flex items-center gap-2 mb-3">
            <Repeat className="w-4 h-4" style={{ color: '#342c82' }} />
            <span className="text-sm font-semibold text-gray-800">Recurring Settings</span>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700 whitespace-nowrap min-w-[60px]">
                Frequency
              </label>
              <div className="relative">
                <select
                  value={frequency}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                  className="dateTime-input appearance-none pr-8 frequency-select"
                  style={{ 
                    backgroundColor: 'white', 
                    minWidth: '120px'
                  }}
                >
                  {frequencyOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {renderRecurrenceOptions()}

            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700 whitespace-nowrap min-w-[60px]">
                Until
              </label>
              <div className="relative">
                <DatePicker
                  selected={untilDate}
                  onChange={handleUntilDateChange}
                  dateFormat={dateFormat} 
                  minDate={internalStartDate}
                  isClearable
                  placeholderText="No end date"
                  className="dateTime-input"
                  style={{ 
                    backgroundColor: 'white',
                    minWidth: '140px'
                  }}
                  popperClassName="react-datepicker-popper"
                  popperPlacement="bottom-start"
                  clearButtonClassName="custom-clear-button"
                />
                {!untilDate && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <Calendar className="w-3 h-3 text-gray-400" />
                  </div>
                )}
                {untilDate && (
                  <button
                    onClick={() => handleUntilDateChange(null)}
                    className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

{isRecurring && (
    <div className="mt-4 rounded p-3 text-xs" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="flex items-center gap-2" style={{ color: '#342c82' }}>
          <Clock className="w-3 h-3" />
          <span className="font-medium">
            {
              isAllDay
              ? (isSameDay ? 
                  `All day on ${formatDate(internalStartDate)}`: 
                  `All day from ${formatDate(internalStartDate)} to ${formatDate(internalEndDate)}`)
              : (isSameDay ? 
                `${formatDate(internalStartDate)} ${localTimeFormatted(settings, internalStartDate)} - ${localTimeFormatted(settings, internalEndDate)}`: 
                `${formatDateTime(internalStartDate)} - ${formatDateTime(internalEndDate)}`)
            }
                
            <span className="ml-2 text-gray-600">
              • Repeats {frequency.toLowerCase()}
              {frequency === 'Weekly' && selectedDays.length > 0 && (
                <span> on {selectedDays.map(d => daysOfWeek[d].label).join(', ')}</span>
              )}
              {untilDate && <span> until {formatDate(untilDate)}</span>}
            </span>
          </span>
        </div>
      </div>
)}
    </div>
  );
};

export default MeetingDays;